/**
 * PostgREST typed accessors for staff / staff_shifts / commission_rules /
 * commissions and the v_commission_summary view (plan 08 Tasks 4/6/8/10, plan
 * 18 Tasks 6/7/8; ST-01/02/03). RLS scopes every row to the caller's tenant and
 * role: owner/manager manage staff + rules; a staff member sees their own shifts
 * and own commissions. Writes carry tenant_id (the RLS `with check` requires it
 * to equal auth.tenant_id()). Mutations that must audit (clock-in/out, accrual)
 * go through the staff-ops Edge Function instead.
 *
 * NOTE: v_commission_summary is created by migration 0102 and lands in
 * @gym/db-types only after the central regen; it is accessed through a
 * schema-relaxed client until then (see listCommissionSummary).
 */
import type { Role } from '@gym/shared';
import type { SupabaseClient } from '@supabase/supabase-js';

import type { GymSupabaseClient } from '../supabase.js';

/** Untyped escape hatch for the not-yet-generated v_commission_summary view. */
function untyped(client: GymSupabaseClient): SupabaseClient {
  return client as unknown as SupabaseClient;
}

// ── Staff profiles (ST-01) ──────────────────────────────────────────────────

export interface ListStaffOptions {
  isActive?: boolean;
  appRole?: Role;
  limit?: number;
}

/** List staff for the tenant (owner/manager via RLS); newest first. */
export function listStaff(client: GymSupabaseClient, opts: ListStaffOptions = {}) {
  let q = client.from('staff').select('*').order('created_at', { ascending: false });
  if (opts.isActive !== undefined) q = q.eq('is_active', opts.isActive);
  if (opts.appRole) q = q.eq('app_role', opts.appRole);
  return q.limit(opts.limit ?? 100);
}

/** Single staff row by id (RLS-gated). */
export function getStaff(client: GymSupabaseClient, id: string) {
  return client.from('staff').select('*').eq('id', id).single();
}

/** Active staff usable as instructors (trainer/owner/manager) for assignment pickers. */
export function listInstructors(client: GymSupabaseClient) {
  return client
    .from('staff')
    .select('id, first_name, last_name, app_role')
    .eq('is_active', true)
    .in('app_role', ['trainer', 'owner', 'manager'])
    .order('first_name');
}

export interface CreateStaffInput {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  appRole: Role;
  locale?: 'mk' | 'sq' | 'en' | null;
  hiredAt?: string | null;
  /** Payroll + HR profile (admin-web rework, migration 0143). */
  grossSalary?: number | null; // бруто, minor units (MKD*100)
  netSalary?: number | null; // нето, minor units
  iban?: string | null;
  nationalId?: string | null; // ЕМБГ
  address?: string | null;
  dateOfBirth?: string | null; // YYYY-MM-DD
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  employmentType?: string | null; // 'full_time' | 'part_time'
  notes?: string | null;
}

export type UpdateStaffInput = Partial<CreateStaffInput>;

/** Create a staff profile (owner/manager via RLS). tenant_id pinned to the caller. */
export function createStaff(client: GymSupabaseClient, tenantId: string, input: CreateStaffInput) {
  return client
    .from('staff')
    .insert({
      tenant_id: tenantId,
      first_name: input.firstName,
      last_name: input.lastName,
      email: input.email ?? null,
      phone: input.phone ?? null,
      app_role: input.appRole,
      locale: input.locale ?? null,
      hired_at: input.hiredAt ?? null,
      gross_salary: input.grossSalary ?? null,
      net_salary: input.netSalary ?? null,
      iban: input.iban ?? null,
      national_id: input.nationalId ?? null,
      address: input.address ?? null,
      date_of_birth: input.dateOfBirth ?? null,
      emergency_contact_name: input.emergencyContactName ?? null,
      emergency_contact_phone: input.emergencyContactPhone ?? null,
      employment_type: input.employmentType ?? null,
      notes: input.notes ?? null,
    })
    .select()
    .single();
}

/** Update a staff profile (owner/manager via RLS). */
export function updateStaff(client: GymSupabaseClient, id: string, input: UpdateStaffInput) {
  return client
    .from('staff')
    .update({
      updated_at: new Date().toISOString(),
      ...(input.firstName !== undefined ? { first_name: input.firstName } : {}),
      ...(input.lastName !== undefined ? { last_name: input.lastName } : {}),
      ...(input.email !== undefined ? { email: input.email } : {}),
      ...(input.phone !== undefined ? { phone: input.phone } : {}),
      ...(input.appRole !== undefined ? { app_role: input.appRole } : {}),
      ...(input.locale !== undefined ? { locale: input.locale } : {}),
      ...(input.hiredAt !== undefined ? { hired_at: input.hiredAt } : {}),
      ...(input.grossSalary !== undefined ? { gross_salary: input.grossSalary } : {}),
      ...(input.netSalary !== undefined ? { net_salary: input.netSalary } : {}),
      ...(input.iban !== undefined ? { iban: input.iban } : {}),
      ...(input.nationalId !== undefined ? { national_id: input.nationalId } : {}),
      ...(input.address !== undefined ? { address: input.address } : {}),
      ...(input.dateOfBirth !== undefined ? { date_of_birth: input.dateOfBirth } : {}),
      ...(input.emergencyContactName !== undefined
        ? { emergency_contact_name: input.emergencyContactName }
        : {}),
      ...(input.emergencyContactPhone !== undefined
        ? { emergency_contact_phone: input.emergencyContactPhone }
        : {}),
      ...(input.employmentType !== undefined ? { employment_type: input.employmentType } : {}),
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
    })
    .eq('id', id)
    .select()
    .single();
}

/** Deactivate a staff profile (soft delete — preserves history, plan 08 §6.6). */
export function deactivateStaff(client: GymSupabaseClient, id: string) {
  return client
    .from('staff')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
}

/** Count a staff member's future scheduled shifts (deactivation warning, §6.6). */
export function countFutureShifts(client: GymSupabaseClient, staffId: string) {
  return client
    .from('staff_shifts')
    .select('id', { count: 'exact', head: true })
    .eq('staff_id', staffId)
    .gt('scheduled_start', new Date().toISOString());
}

// ── Shifts (ST-02) ──────────────────────────────────────────────────────────

export interface ShiftRange {
  from: string; // ISO timestamptz inclusive
  to: string; // ISO timestamptz inclusive
  staffId?: string;
}

/** List shifts in a window (owner/manager: all; staff: own via RLS). */
export function listShifts(client: GymSupabaseClient, range: ShiftRange) {
  let q = client
    .from('staff_shifts')
    .select('*')
    .gte('scheduled_start', range.from)
    .lte('scheduled_start', range.to)
    .order('scheduled_start');
  if (range.staffId) q = q.eq('staff_id', range.staffId);
  return q;
}

export interface CreateShiftInput {
  staffId: string;
  locationId?: string | null;
  scheduledStart: string;
  scheduledEnd: string;
}

/** Schedule a shift (owner/manager via RLS). */
export function createShift(client: GymSupabaseClient, tenantId: string, input: CreateShiftInput) {
  return client
    .from('staff_shifts')
    .insert({
      tenant_id: tenantId,
      staff_id: input.staffId,
      location_id: input.locationId ?? null,
      scheduled_start: input.scheduledStart,
      scheduled_end: input.scheduledEnd,
    })
    .select()
    .single();
}

/** Update a scheduled shift (owner/manager via RLS). */
export function updateShift(
  client: GymSupabaseClient,
  id: string,
  input: Partial<CreateShiftInput>,
) {
  return client
    .from('staff_shifts')
    .update({
      updated_at: new Date().toISOString(),
      ...(input.staffId !== undefined ? { staff_id: input.staffId } : {}),
      ...(input.locationId !== undefined ? { location_id: input.locationId } : {}),
      ...(input.scheduledStart !== undefined ? { scheduled_start: input.scheduledStart } : {}),
      ...(input.scheduledEnd !== undefined ? { scheduled_end: input.scheduledEnd } : {}),
    })
    .eq('id', id)
    .select()
    .single();
}

/** Delete a scheduled shift (owner/manager via RLS). */
export function deleteShift(client: GymSupabaseClient, id: string) {
  return client.from('staff_shifts').delete().eq('id', id);
}

// ── Commission rules (ST-03) ────────────────────────────────────────────────

/** List active commission rules (owner/manager via RLS). */
export function listCommissionRules(
  client: GymSupabaseClient,
  opts: { activeOnly?: boolean } = {},
) {
  let q = client.from('commission_rules').select('*').order('created_at', { ascending: false });
  if (opts.activeOnly) q = q.eq('is_active', true);
  return q;
}

export interface CreateCommissionRuleInput {
  staffId?: string | null;
  appRole?: Role | null;
  basis: 'membership_sold' | 'pt_session' | 'retail_sale' | 'class_taught';
  rateBps?: number | null;
  flatAmount?: number | null;
  isActive?: boolean;
}

/** Create a commission rule (owner/manager via RLS). */
export function createCommissionRule(
  client: GymSupabaseClient,
  tenantId: string,
  input: CreateCommissionRuleInput,
) {
  return client
    .from('commission_rules')
    .insert({
      tenant_id: tenantId,
      staff_id: input.staffId ?? null,
      app_role: input.appRole ?? null,
      basis: input.basis,
      rate_bps: input.rateBps ?? null,
      flat_amount: input.flatAmount ?? null,
      is_active: input.isActive ?? true,
    })
    .select()
    .single();
}

/** Update a commission rule (owner/manager via RLS). */
export function updateCommissionRule(
  client: GymSupabaseClient,
  id: string,
  input: Partial<CreateCommissionRuleInput>,
) {
  return client
    .from('commission_rules')
    .update({
      updated_at: new Date().toISOString(),
      ...(input.staffId !== undefined ? { staff_id: input.staffId } : {}),
      ...(input.appRole !== undefined ? { app_role: input.appRole } : {}),
      ...(input.basis !== undefined ? { basis: input.basis } : {}),
      ...(input.rateBps !== undefined ? { rate_bps: input.rateBps } : {}),
      ...(input.flatAmount !== undefined ? { flat_amount: input.flatAmount } : {}),
      ...(input.isActive !== undefined ? { is_active: input.isActive } : {}),
    })
    .eq('id', id)
    .select()
    .single();
}

/** Deactivate a commission rule (preserved for history; excluded from accrual). */
export function deactivateCommissionRule(client: GymSupabaseClient, id: string) {
  return client
    .from('commission_rules')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
}

// ── Commissions / summary (ST-04, BA-04) ────────────────────────────────────

/** Raw earned commission rows for a staff member (owner/manager; own via RLS). */
export function listCommissions(client: GymSupabaseClient, staffId: string) {
  return client
    .from('commissions')
    .select('*')
    .eq('staff_id', staffId)
    .order('accrued_at', { ascending: false });
}

export interface CommissionSummaryFilter {
  from?: string;
  to?: string;
  staffId?: string;
}

/**
 * Per-staff per-basis commission summary, DATE-AWARE (ST-04). Calls
 * fn_commission_summary, which filters public.commissions on accrued_at BEFORE
 * aggregating — so total_amount / transaction_count are the WINDOWED totals for
 * [from, to], NOT lifetime totals that merely overlap the window (the old
 * gte(latest_accrual)/lte(earliest_accrual) view path reported wrong numbers).
 * RLS on commissions is inherited (SECURITY INVOKER fn). Accessed via the
 * schema-relaxed client until the fn lands in @gym/db-types (regen after 0102).
 */
export function listCommissionSummary(
  client: GymSupabaseClient,
  opts: CommissionSummaryFilter & { tenantId: string },
) {
  return untyped(client).rpc('fn_commission_summary', {
    p_tenant_id: opts.tenantId,
    p_from: opts.from ?? null,
    p_to: opts.to ?? null,
    p_staff_id: opts.staffId ?? null,
  });
}
